// Agent Performance Analytics UI (Polished)
// Additions: per-agent accuracy bar chart, calibration chart (predicted vs actual),
// cost trend, throughput gauges, override rate trend.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, Alert,
} from '@mui/material';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, TrendChart, SimpleBarChart, RadialGauge,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    apAccuracy, apConfidence, apOverrideRate, apCostPerOp, apThroughput, apBenchmarker,
} from './api';

const TABS = ['Accuracy Dashboard', 'Confidence Calibration', 'Override Rates', 'Cost per Operation', 'Throughput & Latency', 'Model Benchmarker'];

function fmtUsd(n) { return n != null ? `$${Number(n).toFixed(4)}` : '—'; }

export default function AgentPerformanceAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [accuracy, setAccuracy] = useState(null);
    const [calibration, setCalibration] = useState(null);
    const [overrides, setOverrides] = useState(null);
    const [costData, setCostData] = useState(null);
    const [throughput, setThroughput] = useState(null);
    const [benchmark, setBenchmark] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !accuracy) { const r = await run(() => apAccuracy()); if (r?.data?.success) setAccuracy(r.data.data); }
            else if (tab === 1 && !calibration) { const r = await run(() => apConfidence()); if (r?.data?.success) setCalibration(r.data.data); }
            else if (tab === 2 && !overrides) { const r = await run(() => apOverrideRate()); if (r?.data?.success) setOverrides(r.data.data); }
            else if (tab === 3 && !costData) { const r = await run(() => apCostPerOp()); if (r?.data?.success) setCostData(r.data.data); }
            else if (tab === 4 && !throughput) { const r = await run(() => apThroughput()); if (r?.data?.success) setThroughput(r.data.data); }
            else if (tab === 5 && !benchmark) { const r = await run(() => apBenchmarker()); if (r?.data?.success) setBenchmark(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const avgAccuracy = (accuracy?.agents || []).length > 0
        ? Math.round(accuracy.agents.reduce((s, a) => s + a.accuracy_pct, 0) / accuracy.agents.length)
        : null;

    const summaryCards = [
        { label: 'Fleet Accuracy', value: avgAccuracy != null ? `${avgAccuracy}%` : '—', accent: avgAccuracy >= 90 ? 'success' : avgAccuracy >= 70 ? 'warning' : 'danger' },
        { label: 'Override Rate', value: overrides?.override_rate_pct != null ? `${overrides.override_rate_pct}%` : '—', accent: overrides?.alert ? 'danger' : 'success' },
        { label: '30d Cost', value: fmtUsd(costData?.total_cost_usd), accent: 'info' },
        { label: 'Calibration', value: calibration?.well_calibrated ? 'Good' : 'Needs Adjust', accent: calibration?.well_calibrated ? 'success' : 'warning' },
    ];

    // Accuracy bar chart
    const accuracyBarData = (accuracy?.agents || []).map(a => ({
        agent: a.agent_name?.substring(0, 18),
        accuracy: a.accuracy_pct,
    }));

    // Calibration chart (predicted vs actual)
    const calibrationChartData = (calibration?.calibration || []).map(c => ({
        bucket: c.bucket,
        predicted: c.predicted_accuracy * 100,
        actual: c.actual_accuracy * 100,
    }));

    const renderTab = () => {
        switch (tab) {
            case 0:
                return accuracy ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Per-Agent Accuracy Dashboard" subtitle="Decision accuracy, overrides, confidence, cost" />
                        {accuracyBarData.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Accuracy by Agent</Typography>
                                <SimpleBarChart
                                    data={accuracyBarData}
                                    xKey="agent"
                                    series={[{ key: 'accuracy', label: 'Accuracy %', color: CHART_COLORS.primary }]}
                                    height={280}
                                    yFormatter={v => `${v}%`}
                                    showLegend={false}
                                    layout="vertical"
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'agent_name', label: 'Agent' },
                                { key: 'total_decisions', label: 'Decisions', align: 'right' },
                                { key: 'accuracy_pct', label: 'Accuracy', align: 'right', render: r => (
                                    <Chip size="small" color={r.accuracy_pct >= 90 ? 'success' : r.accuracy_pct >= 70 ? 'warning' : 'error'} label={`${r.accuracy_pct}%`} />
                                )},
                                { key: 'overridden', label: 'Overrides', align: 'right' },
                                { key: 'avg_confidence', label: 'Conf', align: 'right' },
                                { key: 'avg_cost_usd', label: 'Cost/Run', align: 'right', render: r => fmtUsd(r.avg_cost_usd) },
                            ]}
                            rows={accuracy.agents || []}
                            rowKeyField="agent_definition_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 1:
                return calibration ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Confidence Calibration" subtitle="Predicted accuracy vs actual across confidence buckets" />
                        <SummaryGrid cards={[
                            { label: 'Calibration Status', value: calibration.well_calibrated ? 'Good' : 'Drift Detected', accent: calibration.well_calibrated ? 'success' : 'warning' },
                            { label: 'Avg Gap', value: calibration.avg_calibration_gap != null ? `${(calibration.avg_calibration_gap * 100).toFixed(1)}%` : '—', accent: 'info' },
                        ]} cols={2} />
                        {calibrationChartData.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Predicted vs Actual Accuracy</Typography>
                                <SimpleBarChart
                                    data={calibrationChartData}
                                    xKey="bucket"
                                    series={[
                                        { key: 'predicted', label: 'Predicted', color: CHART_COLORS.secondary },
                                        { key: 'actual', label: 'Actual', color: CHART_COLORS.primary },
                                    ]}
                                    height={260}
                                    yFormatter={v => `${v}%`}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'bucket', label: 'Bucket' },
                                { key: 'predicted_accuracy', label: 'Predicted', align: 'right', render: r => `${(r.predicted_accuracy * 100).toFixed(0)}%` },
                                { key: 'actual_accuracy', label: 'Actual', align: 'right', render: r => `${(r.actual_accuracy * 100).toFixed(0)}%` },
                                { key: 'gap', label: 'Gap', align: 'right', render: r => <Chip size="small" color={Math.abs(r.gap) < 0.1 ? 'success' : 'warning'} label={`${(r.gap * 100).toFixed(0)}%`} /> },
                                { key: 'sample_size', label: 'Samples', align: 'right' },
                            ]}
                            rows={calibration.calibration || []}
                            rowKeyField="bucket"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return overrides ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Human Override Rate" subtitle="Track how often humans override agent decisions" />
                        <SummaryGrid cards={[
                            { label: 'Override Rate', value: `${overrides.override_rate_pct}%`, accent: overrides.alert ? 'danger' : 'success' },
                            { label: 'Total Runs', value: overrides.total_runs, accent: 'info' },
                            { label: 'Total Overrides', value: overrides.total_overrides, accent: 'warning' },
                        ]} cols={3} />
                        {overrides.alert_message && <Alert severity="warning">{overrides.alert_message}</Alert>}
                        {(overrides.weekly_trend || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Weekly Override Rate Trend</Typography>
                                <TrendChart
                                    data={overrides.weekly_trend}
                                    xKey="week"
                                    series={[{ key: 'rate_pct', label: 'Rate %', color: CHART_COLORS.warning }]}
                                    height={240}
                                    type="area"
                                    yFormatter={v => `${v}%`}
                                />
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 3:
                return costData ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Cost per Operation" subtitle="Claude/Gemini API usage cost tracking" />
                        <SummaryGrid cards={[
                            { label: 'Total 30d Cost', value: fmtUsd(costData.total_cost_usd), accent: 'primary' },
                            { label: 'Models Active', value: (costData.by_model || []).length, accent: 'info' },
                        ]} cols={2} />
                        {(costData.by_model || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Cost by Model</Typography>
                                <SimpleBarChart
                                    data={costData.by_model.map(m => ({ model: m.model, cost: m.total_cost_usd }))}
                                    xKey="model"
                                    series={[{ key: 'cost', label: 'Cost $', color: CHART_COLORS.secondary }]}
                                    height={240}
                                    yFormatter={v => `$${v.toFixed(2)}`}
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'model', label: 'Model' },
                                { key: 'calls', label: 'Calls', align: 'right' },
                                { key: 'total_input_tokens', label: 'Input Tokens', align: 'right', render: r => r.total_input_tokens?.toLocaleString() },
                                { key: 'total_output_tokens', label: 'Output Tokens', align: 'right', render: r => r.total_output_tokens?.toLocaleString() },
                                { key: 'total_cost_usd', label: 'Total Cost', align: 'right', render: r => fmtUsd(r.total_cost_usd) },
                                { key: 'avg_cost_per_call', label: 'Avg/Call', align: 'right', render: r => fmtUsd(r.avg_cost_per_call) },
                            ]}
                            rows={costData.by_model || []}
                            rowKeyField="model"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 4:
                return throughput ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Throughput & Latency" subtitle="Agent processing speed and capacity" />
                        <SummaryGrid cards={[
                            { label: 'Runs (24h)', value: throughput.total_runs, accent: 'primary' },
                            { label: 'Runs/Hour', value: throughput.runs_per_hour, accent: 'info' },
                            { label: 'Avg Iterations', value: throughput.avg_iterations, accent: 'secondary' },
                            { label: 'Tool Avg Latency', value: `${throughput.tool_avg_latency_ms}ms`, accent: throughput.tool_avg_latency_ms > 1000 ? 'warning' : 'success' },
                        ]} />
                        {throughput.peak_hour && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Peak Hour</Typography>
                                <Typography variant="body1">
                                    {throughput.peak_hour.hour} — <b>{throughput.peak_hour.runs}</b> runs
                                </Typography>
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return benchmark ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Model Benchmarker" subtitle="Compare models against test suite" />
                        {benchmark.recommended && <Chip size="small" color="success" label={`Recommended: ${benchmark.recommended}`} />}
                        <AIAnalysisBox text={benchmark.ai_recommendation} />
                        {(benchmark.models || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Model Comparison</Typography>
                                <SimpleBarChart
                                    data={benchmark.models.map(m => ({
                                        model: m.model,
                                        success: m.success_rate_pct,
                                        confidence: (m.avg_confidence || 0) * 100,
                                    }))}
                                    xKey="model"
                                    series={[
                                        { key: 'success', label: 'Success %', color: CHART_COLORS.success },
                                        { key: 'confidence', label: 'Confidence %', color: CHART_COLORS.secondary },
                                    ]}
                                    height={260}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'model', label: 'Model' },
                                { key: 'total_runs', label: 'Runs', align: 'right' },
                                { key: 'success_rate_pct', label: 'Success', align: 'right', render: r => `${r.success_rate_pct}%` },
                                { key: 'avg_confidence', label: 'Conf', align: 'right' },
                                { key: 'avg_cost_usd', label: 'Cost', align: 'right', render: r => fmtUsd(r.avg_cost_usd) },
                            ]}
                            rows={benchmark.models || []}
                            rowKeyField="model"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Agent Performance Analytics"
            subtitle="Agentic Learning — accuracy, calibration, overrides, cost, throughput, benchmarks."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
